import { css } from 'lit';

/**
 * Shared, Shadow-DOM-scoped styles for the auth forms. Colors are exposed as CSS custom properties
 * so pages (and the eventual app shell/theme) can override them without piercing the shadow root.
 */
export const formStyles = css`
  :host {
    display: block;
    font-family: system-ui, sans-serif;
    color: var(--gc-fg, #1a1a1a);
  }
  form {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
    max-width: 22rem;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    font-size: 0.9rem;
  }
  input {
    padding: 0.55rem 0.65rem;
    border: 1px solid var(--gc-border, #c4c7d0);
    border-radius: 0.4rem;
    font-size: 1rem;
  }
  input:focus-visible {
    outline: 2px solid var(--gc-accent, #4361ee);
    outline-offset: 1px;
  }
  button {
    padding: 0.6rem 0.9rem;
    border: none;
    border-radius: 0.4rem;
    background: var(--gc-accent, #4361ee);
    color: #fff;
    font-size: 1rem;
    cursor: pointer;
  }
  button:disabled {
    opacity: 0.6;
    cursor: progress;
  }
  .error {
    color: var(--gc-error, #c1121f);
    font-size: 0.9rem;
    min-height: 1.2em;
  }
  .success {
    color: var(--gc-success, #2a9d8f);
    font-size: 0.9rem;
  }
  p {
    margin: 0;
    font-size: 0.9rem;
  }
  /* A checkbox + label on one row (e.g. "Remember this device"). */
  label.checkbox {
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
  }
  label.checkbox input {
    width: auto;
  }
  /* A secondary, link-styled button (e.g. "Use a recovery code instead"). */
  button.link {
    align-self: flex-start;
    padding: 0.25rem 0;
    background: none;
    border: none;
    color: var(--gc-accent, #4361ee);
    font-size: 0.9rem;
    text-decoration: underline;
    cursor: pointer;
  }
`;

/**
 * Game/replay-only suppression of iOS double-tap-zoom.
 *
 * iOS only honours `touch-action` on the *actually-tapped* element — an ancestor's value is ignored —
 * and the tapped element almost always lives inside a component's shadow root, where neither an
 * outer-page `*` rule nor a non-inherited property can reach it. So every component in the game/replay
 * tree spreads this `*` rule into its own shadow root (the gc-game-table / gc-game-replay hosts, plus
 * each child component they render).
 *
 * It's gated on the inherited `--gc-touch-action` custom property so it stays scoped to the game/replay
 * UX: custom properties inherit across shadow boundaries, so it resolves to `manipulation` only because
 * the gc-game-table / gc-game-replay hosts set `--gc-touch-action: manipulation`, and falls back to
 * `auto` (a no-op) everywhere else — e.g. gc-score-graph in a profile/lobby card, gc-update-banner in the
 * lobby, or gc-reaction-customizer in account settings. The `*` selector's zero specificity also lets more
 * specific rules win (e.g. gc-score-graph's `.plot { touch-action: pan-y }`), so scrolling/panning is kept.
 */
export const gameTouchAction = css`
  * {
    touch-action: var(--gc-touch-action, auto);
  }
`;
